/**
 * Map campaign API errors to user-friendly copy.
 * Never surface backend field names (segment_ids, extra_customer_ids, etc.).
 */

const TITLE_MESSAGES = {
  AudienceInvalid: 'Some recipients are missing a phone number or email. Fix contacts or change the audience.',
  CampaignNotEditable: 'Only draft campaigns can be edited.',
  CampaignNotDeletable: 'Only draft campaigns can be deleted.',
  CampaignNotDraft: 'Only draft campaigns can be sent or scheduled.',
  CampaignNotScheduled: 'Only scheduled campaigns can be unscheduled.',
  CampaignSendEnqueueFailed: 'Could not start the send. The campaign was returned to draft — try again.',
  CampaignScheduleEnqueueFailed: 'Could not schedule the campaign. It was returned to draft — try again.',
  CampaignScheduleInProgress: 'This campaign is still being scheduled. Try unscheduling again in a moment.',
  SubjectRequired: 'Email campaigns need a subject line.',
  BodyRequired: 'Add a message before sending.',
  InvalidSendAt: 'Pick a time in the future, or choose Send now.',
  NotFound: 'This campaign could not be found.',
  permission_denied: 'You do not have permission to manage campaigns.',
};

const FIELD_LABEL_HINTS = {
  subject: 'subject',
  body: 'message',
  name: 'name',
  channel: 'channel',
  coupon: 'coupon',
  segment_ids: 'segments',
  extra_customer_ids: 'customers',
  send_at: 'send time',
};

/** Strip snake_case / camelCase API keys from a string for display. */
function scrubFieldNames(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/\b(segment_ids|extra_customer_ids|coupon_id|send_at|created_by|celery_task_id)\b/gi, 'selection')
    .replace(/\bnon_field_errors\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function messageFromErrorEntry(entry) {
  if (entry == null) return '';
  if (typeof entry === 'string') return scrubFieldNames(entry);
  if (Array.isArray(entry)) {
    return entry.map(messageFromErrorEntry).filter(Boolean).join(' ');
  }
  if (typeof entry === 'object') {
    // { subject: "..." } or { subject: ["..."] } — use values only, never keys
    return Object.values(entry)
      .map(messageFromErrorEntry)
      .filter(Boolean)
      .join(' ');
  }
  return scrubFieldNames(String(entry));
}

function humanizeKnownValidation(text) {
  if (!text) return '';
  const lower = text.toLowerCase();
  if (lower.includes('unknown segments')) {
    return 'One or more selected segments are invalid. Refresh and try again.';
  }
  if (lower.includes('unknown customers')) {
    return 'One or more selected customers are invalid. Refresh and try again.';
  }
  if (lower.includes('does not belong to this organization') || lower.includes('not available for this organization')) {
    return 'That coupon is not available for this organization.';
  }
  if (lower.includes('channel must be') || lower.includes('choose sms or email')) {
    return 'Choose SMS or Email as the channel.';
  }
  if (lower.includes('message body is required') || lower.includes('body is required') || lower.includes('add a message')) {
    return 'Add a message before saving.';
  }
  if (lower.includes('subject is required') || lower.includes('need a subject')) {
    return 'Email campaigns need a subject line.';
  }
  if (lower.includes('send_at') || lower.includes('in the future') || lower.includes('use send for asap')) {
    return 'Pick a time in the future, or choose Send now.';
  }
  return scrubFieldNames(text);
}

/**
 * @param {unknown} error - axios error or response-like object
 * @param {string} fallback
 * @returns {string}
 */
export function getCampaignErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const data = error?.response?.data || error?.data || error || {};
  const title = data.title || data.code || '';

  if (title && TITLE_MESSAGES[title]) {
    // Prefer explicit detail when it is already a full sentence (audience errors include counts)
    const detail = typeof data.detail === 'string' ? data.detail.trim() : '';
    if (title === 'AudienceInvalid' && detail) {
      return scrubFieldNames(detail);
    }
    return TITLE_MESSAGES[title];
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    return humanizeKnownValidation(data.detail);
  }

  if (data.errors) {
    const extracted = messageFromErrorEntry(data.errors);
    const friendly = humanizeKnownValidation(extracted);
    if (friendly) return friendly;
  }

  // Ant Design form validateFields rejection
  if (error?.errorFields?.length) {
    const msgs = error.errorFields
      .flatMap((f) => f.errors || [])
      .map((m) => humanizeKnownValidation(m))
      .filter(Boolean);
    if (msgs.length) return msgs[0];
  }

  if (typeof error?.message === 'string'
    && error.message
    && !error.message.startsWith('Request failed with status')) {
    return scrubFieldNames(error.message);
  }

  return fallback;
}

/** Optional: map form field names to labels if needed elsewhere */
export function campaignFieldLabel(field) {
  return FIELD_LABEL_HINTS[field] || 'field';
}
